import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../utils/supabase';
import { EloChange, MobileSearchField } from '../common';
import Button from '../common/Button';
import { Text } from '../common/Typography';
import { teamHighlightClass } from './gameHistoryRowStyles';
import { detectGameType } from '../../utils/gameTypeUtils';
import {
  checkGameCorrectionEligibility,
  getEditScoreDisabledTooltip,
  POST_SESSION_CORRECTION_WINDOW_HOURS,
} from '../../utils/scoreCorrection';
import { fetchPostSessionCorrectionGates } from '../../services/scoreCorrectionService';
import {
  fetchScoreCorrectionMetaBySessionIds,
  hasPostSessionScoreCorrection,
} from '../../services/scoreCorrectionMetaService';
import { mergeLocalSessionGames } from '../../utils/session/buildLocalSessionGames';
import { isBrowserOffline } from '../../utils/session/offlineQueueConstants';
import EditCompetitiveGameModal from './EditCompetitiveGameModal';
import ScoreCorrectedBadge from './ScoreCorrectedBadge';


/** Player can be included in a new scheduled rematch while still in the session. */
const REMATCH_ELIGIBLE_STATUSES = new Set(['active', 'paused', 'signed_up']);

function getRematchTeamsFromGame(game) {
  const rows = game?.players_session_games;
  if (!Array.isArray(rows) || rows.length === 0) {
    return { team1: [], team2: [], ok: false };
  }
  const team1 = rows.filter((r) => r.team_number === 1).map((r) => r.player_id);
  const team2 = rows.filter((r) => r.team_number === 2).map((r) => r.player_id);
  const singles = team1.length === 1 && team2.length === 1;
  const doubles = team1.length === 2 && team2.length === 2;
  return { team1, team2, ok: singles || doubles };
}

function allParticipantsEligibleForRematch(playerIds, sessionPlayerStatus) {
  if (!sessionPlayerStatus || !playerIds.length) return false;
  return playerIds.every((id) => REMATCH_ELIGIBLE_STATUSES.has(sessionPlayerStatus[id]));
}

function anyRematchPlayerInScheduledGame(playerIds, sessionPlayerScheduledGames) {
  if (!sessionPlayerScheduledGames || !playerIds.length) return false;
  return playerIds.some((id) => Boolean(sessionPlayerScheduledGames[id]));
}

function getTeamPlayers(game, teamNumber) {
  return (game.players_session_games || [])
    .filter((psg) => psg.team_number === teamNumber)
    .map((psg) => psg.players)
    .filter(Boolean);
}

function getViewerTeamNumber(game, viewerId) {
  if (!viewerId) return null;
  const row = (game.players_session_games || []).find((psg) => psg.player_id === viewerId);
  return row?.team_number ?? null;
}

function isHostAlteredGame(game) {
  const method = game?.game_create_method;
  if (!method || typeof method !== 'string') return false;
  return method.includes('_altered') || method === 'authoritative';
}

function getPlayerEloChange(game, playerId) {
  if (!playerId) return null;
  const rows = (game.elo_history || []).filter((eh) => eh.player_id === playerId);
  if (!rows.length) return null;
  const participation = (game.players_session_games || []).find((row) => row.player_id === playerId);
  const matched = participation?.pair_type
    ? rows.find((eh) => eh.pair_type === participation.pair_type) || rows[0]
    : rows[0];
  if (matched?.elo_change == null) return null;
  const change = Number(matched.elo_change);
  return Number.isFinite(change) ? change : null;
}

function AlteredLineupIcon() {
  return (
    <span
      className="inline-flex shrink-0 text-warning"
      title="Lineup was changed after this game was created"
      aria-label="Altered lineup"
    >
      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
      </svg>
    </span>
  );
}

function sessionPlayerNameColor(gender) {
  if (gender?.toLowerCase() === 'female') return 'text-female font-semibold';
  return 'text-text-primary font-semibold';
}

function EditScoreButton({ eligible, disabledReason, onEdit }) {
  const [showReason, setShowReason] = useState(false);

  if (eligible) {
    return (
      <Button
        type="button"
        variant="outline"
        size="small"
        className="shrink-0 text-xs"
        onClick={onEdit}
      >
        Edit score
      </Button>
    );
  }

  return (
    <span
      className="relative inline-flex shrink-0 cursor-not-allowed"
      onMouseEnter={() => disabledReason && setShowReason(true)}
      onMouseLeave={() => setShowReason(false)}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabledReason) setShowReason((open) => !open);
      }}
    >
      <Button type="button" variant="outline" size="small" className="shrink-0 text-xs pointer-events-none" disabled>
        Edit score
      </Button>
      {showReason && disabledReason ? (
        <span
          role="status"
          className="absolute bottom-full right-0 z-20 mb-1.5 w-56 rounded-lg border border-border-medium bg-surface px-3 py-2 text-left text-xs leading-snug text-text-secondary shadow-lg"
        >
          {disabledReason}
        </span>
      ) : null}
    </span>
  );
}

function renderPlayerName(player, { stacked = false } = {}) {
  if (!player) return <span className="text-text-muted">?</span>;
  const first = (player.first_name || '').trim();
  const last = (player.last_name || '').trim();
  const colorClass = sessionPlayerNameColor(player.gender);
  const fullName = `${first} ${last}`.trim() || 'Unknown';
  const compactName = last ? `${first} ${last.charAt(0).toUpperCase()}.` : fullName;

  if (stacked) {
    return <span className={`text-sm ${colorClass}`}>{fullName}</span>;
  }
  return (
    <>
      <span className={`truncate min-w-0 sm:hidden text-xs ${colorClass}`}>{compactName}</span>
      <span className={`truncate min-w-0 hidden sm:inline text-sm ${colorClass}`}>{fullName}</span>
    </>
  );
}

function renderEloDelta(eloChange) {
  if (eloChange === null || eloChange === undefined) return null;
  return (
    <span className="shrink-0">
      <EloChange change={eloChange} showSign size="xs" className="sm:text-sm" />
    </span>
  );
}

function renderTeamLine(players, game, { stacked = false } = {}) {
  if (!players.length) {
    return <span className="text-text-muted">—</span>;
  }
  return (
    <>
      {players.map((player, idx) => (
        <React.Fragment key={player.id || idx}>
          {idx > 0 ? (
            <span className="shrink-0 text-xs font-normal text-text-secondary mx-0.5">+</span>
          ) : null}
          <span className="inline-flex min-w-0 max-w-full items-baseline gap-1">
            {renderPlayerName(player, { stacked })}
            {renderEloDelta(getPlayerEloChange(game, player.id))}
          </span>
        </React.Fragment>
      ))}
    </>
  );
}

function getGameTypeCounts(games) {
  let mensDoubles = 0;
  let womensDoubles = 0;
  let mixedDoubles = 0;
  let unbalancedGames = 0;

  games.forEach((game) => {
    const team1 = getTeamPlayers(game, 1);
    const team2 = getTeamPlayers(game, 2);
    const detected = detectGameType(team1, team2);
    const id = detected?.id;

    if (id === 2) {
      mensDoubles += 1;
    } else if (id === 3) {
      womensDoubles += 1;
    } else if (id === 1) {
      mixedDoubles += 1;
    } else if (id === 6 || id === 8 || id === 9) {
      unbalancedGames += 1;
    }
  });

  return { mensDoubles, womensDoubles, mixedDoubles, unbalancedGames };
}

function playerNameIncludesQuery(player, query) {
  const name = `${player?.first_name || ''} ${player?.last_name || ''} ${player?.name || ''}`.trim().toLowerCase();
  return name.includes(query);
}

function gameMatchesPlayerQuery(game, rawQuery, playersById = null) {
  const q = (rawQuery ?? '').trim().toLowerCase();
  if (!q) return true;
  return (game.players_session_games || []).some((row) => {
    const fromJoin = row.players;
    if (fromJoin && playerNameIncludesQuery(fromJoin, q)) return true;
    const fromMap = playersById?.[row.player_id];
    return Boolean(fromMap && playerNameIncludesQuery(fromMap, q));
  });
}

const PLAYER_SEARCH_FIELD_CLASS =
  'form-input w-full min-w-0 rounded-md border-border-medium bg-field px-2.5 py-1.5 text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-primary sm:max-w-sm';

function formatGameTypeSummary(counts) {
  const parts = [];
  if (counts.mensDoubles) {
    parts.push(`${counts.mensDoubles} men's`);
  }
  if (counts.womensDoubles) {
    parts.push(`${counts.womensDoubles} women's`);
  }
  if (counts.mixedDoubles) {
    parts.push(`${counts.mixedDoubles} mixed`);
  }
  if (counts.unbalancedGames) {
    parts.push(`${counts.unbalancedGames} unbalanced`);
  }
  return parts.join(' · ');
}

/**
 * Session-wide game history for the competitive session Games tab.
 * Neutral team labels (no "You"); highlights the viewer's team when they played.
 */
const SessionGamesHistory = ({
  sessionId,
  /** Player ID (from the players table) of the current viewer — used to highlight their games. */
  viewerPlayerId = null,
  filterPlayerIds = null,
  hideHeader = false,
  showTypeSummary = false,
  canScheduleRematch = false,
  onScheduleRematch = null,
  sessionPlayerStatus = null,
  sessionPlayerScheduledGames = null,
  canCorrectScore = false,
  sessionStatus = null,
  scoringConfig = null,
  playersById = null,
  onScoreCorrected = null,
  refreshKey = 0,
  localGames = null,
}) => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [schedulingRematchGameId, setSchedulingRematchGameId] = useState(null);
  const [editingGame, setEditingGame] = useState(null);
  const [postSessionGates, setPostSessionGates] = useState({
    isLatestClubSession: false,
    withinCorrectionWindow: false,
  });
  const [correctionMetaByGameId, setCorrectionMetaByGameId] = useState(() => new Map());
  const [playerSearchQuery, setPlayerSearchQuery] = useState('');

  const fetchGamesHistory = useCallback(async () => {
    if (!sessionId) return;
    if (isBrowserOffline()) {
      setError(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const { data: gamesData, error: gamesError } = await supabase
        .from('session_games')
        .select(`
          id,
          session_id,
          team_1_score,
          team_2_score,
          winning_team,
          start_time,
          end_time,
          created_at,
          game_create_method,
          game_type_id,
          game_types (
            id,
            name,
            description
          ),
          players_session_games (
            player_id,
            team_number,
            result,
            pair_type,
            players (
              id,
              first_name,
              last_name,
              gender,
              is_guest,
              is_active,
              merged_at,
              guest_merge_declined_at
            )
          ),
          elo_history (
            player_id,
            old_value,
            new_value,
            elo_change,
            pair_type,
            players (
              id,
              first_name,
              last_name
            )
          )
        `)
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (gamesError) {
        console.error('Error fetching games:', gamesError);
        setGames([]);
        setError('Failed to load games history');
        return;
      }

      setGames(gamesData || []);

      try {
        const meta = await fetchScoreCorrectionMetaBySessionIds([sessionId]);
        setCorrectionMetaByGameId(meta);
      } catch (metaErr) {
        console.warn('[SessionGamesHistory] Correction meta unavailable:', metaErr);
      }
    } catch (err) {
      console.error('Error in fetchGamesHistory:', err);
      setGames([]);
      setError('Failed to load games history');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    void fetchGamesHistory();
  }, [fetchGamesHistory, refreshKey]);

  useEffect(() => {
    if (!canCorrectScore || sessionStatus !== 'completed' || !sessionId) {
      setPostSessionGates({ isLatestClubSession: false, withinCorrectionWindow: false });
      return;
    }
    let cancelled = false;
    fetchPostSessionCorrectionGates(sessionId).then((gates) => {
      if (cancelled) return;
      setPostSessionGates({
        isLatestClubSession: Boolean(gates.isLatestClubSession),
        withinCorrectionWindow: Boolean(gates.withinCorrectionWindow),
      });
    });
    return () => {
      cancelled = true;
    };
  }, [canCorrectScore, sessionStatus, sessionId, refreshKey]);

  const showPostSessionCorrectionBanner = hasPostSessionScoreCorrection(correctionMetaByGameId);

  const displayGames = useMemo(
    () => mergeLocalSessionGames(games, Array.isArray(localGames) ? localGames : []),
    [games, localGames]
  );

  const filteredGames = useMemo(() => {
    const bySelectedPlayer = !filterPlayerIds?.length
      ? displayGames
      : displayGames.filter((game) => {
          const playerIds = (game.players_session_games || []).map((row) => row.player_id);
          return filterPlayerIds.some((id) => playerIds.includes(id));
        });
    return bySelectedPlayer.filter((game) =>
      gameMatchesPlayerQuery(game, playerSearchQuery, playersById)
    );
  }, [displayGames, filterPlayerIds, playerSearchQuery, playersById]);

  const typeSummary = useMemo(
    () => (showTypeSummary ? formatGameTypeSummary(getGameTypeCounts(filteredGames)) : ''),
    [filteredGames, showTypeSummary]
  );

  const gamesWithNumbers = useMemo(() => {
    const sorted = [...filteredGames].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );
    const numbered = sorted.map((game, index) => ({ ...game, gameNumber: index + 1 }));
    return numbered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [filteredGames]);

  const handleScheduleRematchClick = useCallback(
    async (game) => {
      if (!onScheduleRematch) return;
      const { team1, team2, ok } = getRematchTeamsFromGame(game);
      if (!ok) return;
      const allIds = [...team1, ...team2];
      if (!allParticipantsEligibleForRematch(allIds, sessionPlayerStatus)) return;
      if (anyRematchPlayerInScheduledGame(allIds, sessionPlayerScheduledGames)) return;
      setSchedulingRematchGameId(game.id);
      try {
        await onScheduleRematch(team1, team2);
      } catch (e) {
        console.error('[SessionGamesHistory] Schedule rematch failed:', e);
      } finally {
        setSchedulingRematchGameId(null);
      }
    },
    [onScheduleRematch, sessionPlayerStatus, sessionPlayerScheduledGames]
  );

  if (loading && displayGames.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="text-xl font-semibold text-primary animate-pulse">Loading games history…</div>
        </div>
      </div>
    );
  }

  if (error && displayGames.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="text-xl font-semibold text-danger-dark">Error loading games</div>
          <p className="mt-2 text-sm text-text-secondary">{error}</p>
        </div>
      </div>
    );
  }

  const playerSearchActive = playerSearchQuery.trim().length > 0;
  const noGamesAtAll = displayGames.length === 0 && !filterPlayerIds?.length && !playerSearchActive;

  if (noGamesAtAll) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <h3 className="mb-2 text-xl font-semibold text-text-primary">No games yet</h3>
          <p className="text-text-secondary">
            Games will appear here once players start playing matches.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!hideHeader ? (
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-text-primary">
              Game history ({filteredGames.length} game{filteredGames.length !== 1 ? 's' : ''})
            </h3>
            {filteredGames.some((game) => game.pendingSync || String(game.id || '').startsWith('local:')) ? (
              <p className="mt-0.5 text-xs text-warning-dark">
                Includes games waiting to save on this device.
              </p>
            ) : null}
          </div>
          <Button type="button" variant="outline" size="small" onClick={() => void fetchGamesHistory()}>
            Refresh
          </Button>
        </div>
      ) : null}

      {showTypeSummary && typeSummary ? (
        <Text className="text-xs text-text-secondary">{typeSummary}</Text>
      ) : null}

      {canCorrectScore && sessionStatus === 'completed' && postSessionGates.isLatestClubSession && postSessionGates.withinCorrectionWindow ? (
        <Text className="text-xs text-text-secondary">
          Scores can be corrected for {POST_SESSION_CORRECTION_WINDOW_HOURS} hours while this is the club’s most recent session.
        </Text>
      ) : null}

      {showPostSessionCorrectionBanner ? (
        <Text className="text-xs text-text-secondary">
          One or more scores in this session were corrected after it ended. Ratings and results reflect the latest scores.
        </Text>
      ) : null}

      <MobileSearchField
        id="session-games-player-search"
        aria-label="Search games by player name"
        value={playerSearchQuery}
        onChange={(e) => setPlayerSearchQuery(e.target.value)}
        placeholder="Search players…"
        className={PLAYER_SEARCH_FIELD_CLASS}
      />

      {filteredGames.length === 0 ? (
        <p className="py-8 text-center text-sm text-text-secondary">
          {playerSearchActive || filterPlayerIds?.length
            ? 'No games match that player. Try another name or clear the search.'
            : 'Games will appear here once players start playing matches.'}
        </p>
      ) : (
      <div className="space-y-2">
        {gamesWithNumbers.map((game) => {
          const team1Players = getTeamPlayers(game, 1);
          const team2Players = getTeamPlayers(game, 2);
          const team1Score = game.team_1_score ?? 0;
          const team2Score = game.team_2_score ?? 0;
          const team1Won = team1Score > team2Score;
          const team2Won = team2Score > team1Score;
          const viewerTeam = getViewerTeamNumber(game, viewerPlayerId);
          const alteredIcon = isHostAlteredGame(game) ? <AlteredLineupIcon /> : null;

          const team1Class = teamHighlightClass(viewerTeam === 1, team1Won);
          const team2Class = teamHighlightClass(viewerTeam === 2, team2Won);

          const { team1: rematchTeam1, team2: rematchTeam2, ok: rematchShapeOk } =
            getRematchTeamsFromGame(game);
          const rematchAllIds = [...rematchTeam1, ...rematchTeam2];
          const rematchPlayersEligible =
            rematchShapeOk && allParticipantsEligibleForRematch(rematchAllIds, sessionPlayerStatus);
          const rematchPlayersInScheduledGame =
            rematchShapeOk &&
            rematchPlayersEligible &&
            anyRematchPlayerInScheduledGame(rematchAllIds, sessionPlayerScheduledGames);
          const rematchDisabledReason = !canScheduleRematch
            ? null
            : game.pendingSync || String(game.id || '').startsWith('local:')
              ? 'Save queued games before scheduling a rematch.'
            : !rematchShapeOk
              ? 'This game does not have a valid singles or doubles lineup to schedule.'
              : !rematchPlayersEligible
                ? 'One or more players have left or finished the session.'
                : rematchPlayersInScheduledGame
                  ? 'One or more players are already in a scheduled match.'
                  : null;

          const correction = canCorrectScore && !game.pendingSync && !String(game.id || '').startsWith('local:')
            ? checkGameCorrectionEligibility(game, {
                sessionStatus,
                allSessionGames: games,
                playersById: playersById || {},
                isLatestClubSession: postSessionGates.isLatestClubSession,
                withinCorrectionWindow: postSessionGates.withinCorrectionWindow,
              })
            : { eligible: false, reason: null };

          const editScoreDisabledReason = correction.eligible
            ? null
            : getEditScoreDisabledTooltip(correction.reason);

          const editScoreButton = canCorrectScore ? (
            <EditScoreButton
              eligible={correction.eligible}
              disabledReason={editScoreDisabledReason}
              onEdit={() => setEditingGame(game)}
            />
          ) : null;

          const rematchButton = canScheduleRematch ? (
            <Button
              type="button"
              variant="secondary"
              size="small"
              className="shrink-0 text-xs"
              disabled={!!rematchDisabledReason || schedulingRematchGameId === game.id}
              title={rematchDisabledReason || 'Add this lineup to scheduled games'}
              onClick={() => void handleScheduleRematchClick(game)}
            >
              {schedulingRematchGameId === game.id ? 'Scheduling…' : 'Schedule rematch'}
            </Button>
          ) : null;

          const correctionMeta = correctionMetaByGameId.get(game.id);
          const editedBadge = correctionMeta ? (
            <ScoreCorrectedBadge
              correctedAt={correctionMeta.correctedAt}
              correctionMode={correctionMeta.correctionMode}
            />
          ) : null;

          return (
            <div
              key={game.id}
              className="rounded-lg border border-border-medium bg-surface px-2 py-2 sm:px-3 sm:py-2.5 lg:px-4"
            >
              {/* Mobile: stacked scoreboard */}
              <div className="sm:hidden">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className="tabular-nums text-xs text-text-muted">(#{game.gameNumber})</span>
                    {alteredIcon}
                    {editedBadge}
                  </span>
                  <div className="flex items-center gap-1.5">
                    {editScoreButton}
                    {rematchButton}
                  </div>
                </div>
                <div className="overflow-hidden rounded-lg border border-border divide-y divide-border">
                  <div className={`flex items-center justify-between gap-3 px-3 py-2.5 ${team1Class}`}>
                    <div className="flex min-w-0 flex-1 flex-col border-l-2 border-team1 pl-2">
                      <div className="flex min-w-0 flex-wrap items-center gap-x-1 leading-snug">
                        {renderTeamLine(team1Players, game, { stacked: true })}
                      </div>
                    </div>
                    <span className="shrink-0 tabular-nums text-base font-bold text-text-primary">
                      {team1Score}
                    </span>
                  </div>
                  <div className={`flex items-center justify-between gap-3 px-3 py-2.5 ${team2Class}`}>
                    <div className="flex min-w-0 flex-1 flex-col border-l-2 border-team2 pl-2">
                      <div className="flex min-w-0 flex-wrap items-center gap-x-1 leading-snug">
                        {renderTeamLine(team2Players, game, { stacked: true })}
                      </div>
                    </div>
                    <span className="shrink-0 tabular-nums text-base font-bold text-text-primary">
                      {team2Score}
                    </span>
                  </div>
                </div>
              </div>

              {/* sm+: single-row layout */}
              <div className="hidden sm:flex items-center gap-2 min-w-0 text-sm">
                <span className="flex shrink-0 items-center gap-1.5">
                  <span className="w-9 text-right tabular-nums text-text-muted">
                    (#{game.gameNumber})
                  </span>
                  {alteredIcon}
                  {editedBadge}
                </span>

                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div
                    className={`flex min-w-0 max-w-[45%] lg:max-w-none lg:flex-initial items-center gap-x-1 rounded-full px-2 py-1 ${team1Class}`}
                  >
                    {renderTeamLine(team1Players, game)}
                  </div>

                  <span className="shrink-0 tabular-nums font-bold text-text-primary whitespace-nowrap text-base">
                    {team1Score}
                    <span className="mx-1 font-normal text-text-muted">-</span>
                    {team2Score}
                  </span>

                  <div
                    className={`flex min-w-0 flex-1 items-center gap-x-1 rounded-full px-2 py-1 ${team2Class}`}
                  >
                    {renderTeamLine(team2Players, game)}
                  </div>
                </div>

                {editScoreButton || rematchButton ? (
                  <div className="pl-1 shrink-0 flex items-center gap-1.5">
                    {editScoreButton}
                    {rematchButton}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {editingGame ? (
        <EditCompetitiveGameModal
          game={editingGame}
          playersById={playersById || {}}
          scoringConfig={scoringConfig}
          correctionMode={
            checkGameCorrectionEligibility(editingGame, {
              sessionStatus,
              allSessionGames: games,
              isLatestClubSession: postSessionGates.isLatestClubSession,
              withinCorrectionWindow: postSessionGates.withinCorrectionWindow,
            }).mode || (sessionStatus === 'completed' ? 'post_session' : 'live')
          }
          allSessionGames={games}
          onClose={() => setEditingGame(null)}
          onCorrected={async () => {
            setEditingGame(null);
            await fetchGamesHistory();
            if (onScoreCorrected) await onScoreCorrected();
          }}
        />
      ) : null}
    </div>
  );
};

export default SessionGamesHistory;
